// SPDX-License-Identifier: GPL-3.0-or-later

import { detectFiles, openFile, type AppFiles } from '../fs/files';
import type { JourneyInputs } from '../onboarding/journey';
import { removeApplication, updateStatus } from '../store/applications';
import {
  STATUSES,
  type Application,
  type Manifest,
  type Status,
} from '../types';
import { renderBulkActions } from './BulkActions';
import { renderJourneyWizard } from './JourneyWizard';
import { el } from './dom';

type Filter = 'All' | 'Active' | Status;

const FILTER_STORAGE_KEY = 'applify:dashboard-filter';

function statusClass(s: Status): string {
  return s.toLowerCase();
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function relativeDate(iso: string): string {
  try {
    const d = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.round((now - d) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86_400) return `${Math.floor(diff / 3600)}h ago`;
    const days = Math.floor(diff / 86_400);
    if (days < 7) return `${days}d ago`;
    return formatDate(iso);
  } catch {
    return iso;
  }
}

function isEasyApply(t: Application['applyType']): boolean {
  return typeof t === 'string' && t.toLowerCase().includes('easy');
}

function countBy<T extends string>(apps: Application[]): Record<Status, number> {
  const out: Record<Status, number> = {
    Saved: 0, Applied: 0, Interview: 0, Rejected: 0, Offer: 0,
    Discarded: 0, Closed: 0, Filtered: 0,
  };
  for (const a of apps) if (a.status in out) out[a.status]++;
  return out;
}

function loadFilter(): Filter {
  try {
    const v = localStorage.getItem(FILTER_STORAGE_KEY);
    if (!v) return 'Active';
    if (v === 'All' || v === 'Active') return v;
    if ((STATUSES as readonly string[]).includes(v)) return v as Status;
    return 'Active';
  } catch { return 'Active'; }
}

function saveFilter(f: Filter): void {
  try { localStorage.setItem(FILTER_STORAGE_KEY, f); } catch { /* private mode */ }
}

export function resetDashboardFilter(): void {
  saveFilter('Active');
}

function passesFilter(app: Application, f: Filter): boolean {
  if (f === 'All') return true;
  if (f === 'Active') return app.status === 'Saved' || app.status === 'Applied' || app.status === 'Interview';
  return app.status === f;
}

function renderMetrics(apps: Application[]): HTMLElement {
  const total = apps.length;
  const easy = apps.filter((a) => isEasyApply(a.applyType)).length;
  const by = countBy(apps);
  const active = by.Applied + by.Interview;
  const responseRate = (by.Applied + by.Interview + by.Offer) > 0
    ? Math.round((by.Interview + by.Offer) / (by.Applied + by.Interview + by.Offer + by.Rejected || 1) * 100)
    : 0;

  return el(
    'section',
    { class: 'metric-strip' },
    el(
      'div',
      { class: 'metric' },
      el('div', { class: 'label' }, 'Total'),
      el('div', { class: 'number' }, String(total)),
      el(
        'div',
        { class: 'sub' },
        `${easy} Easy Apply · ${total - easy} External`,
      ),
    ),
    el(
      'div',
      { class: 'metric' },
      el('div', { class: 'label' }, 'In motion'),
      el(
        'div',
        { class: 'number' },
        String(active),
        el('small', {}, total > 0 ? `/${total}` : ''),
      ),
      el(
        'div',
        { class: 'sub' },
        `${by.Applied} applied · ${by.Interview} interviewing`,
      ),
    ),
    el(
      'div',
      { class: 'metric' },
      el('div', { class: 'label' }, 'Wins'),
      el('div', { class: 'number' }, String(by.Offer)),
      el(
        'div',
        { class: 'sub' },
        `${by.Rejected} rejected · response rate ${responseRate}%`,
      ),
    ),
    renderPipeline(by, total),
  );
}

function renderPipeline(by: Record<Status, number>, total: number): HTMLElement {
  const all: { status: Status; count: number }[] = [
    { status: 'Saved', count: by.Saved },
    { status: 'Applied', count: by.Applied },
    { status: 'Interview', count: by.Interview },
    { status: 'Offer', count: by.Offer },
    { status: 'Rejected', count: by.Rejected },
    { status: 'Closed', count: by.Closed },
    { status: 'Filtered', count: by.Filtered },
    { status: 'Discarded', count: by.Discarded },
  ];
  const segments = all.filter((s) => s.count > 0);

  const bar = el('div', { class: 'pipeline' });
  const legend = el('div', { class: 'pipeline-legend' });
  for (const s of segments) {
    const pct = total === 0 ? 0 : (s.count / total) * 100;
    bar.appendChild(
      el('span', {
        class: `bg-${statusClass(s.status)}`,
        style: `width: ${pct}%;`,
        title: `${s.status} — ${s.count}`,
      }),
    );
    legend.appendChild(
      el(
        'span',
        { class: 'item' },
        el('span', { class: `dot ${statusClass(s.status)}` }),
        s.status,
        el('span', { class: 'text-faint', style: 'margin-left: 0.2rem;' }, String(s.count)),
      ),
    );
  }

  return el(
    'div',
    { class: 'metric' },
    el('div', { class: 'label' }, 'Pipeline'),
    bar,
    legend,
  );
}

function renderFilterBar(
  apps: Application[],
  current: Filter,
  onChange: (f: Filter) => void,
): HTMLElement {
  const by = countBy(apps);
  const totals: { key: Filter; label: string; count: number; dot?: Status }[] = [
    { key: 'Active', label: 'Active', count: by.Saved + by.Applied + by.Interview },
    { key: 'All', label: 'All', count: apps.length },
    { key: 'Saved', label: 'Saved', count: by.Saved, dot: 'Saved' },
    { key: 'Applied', label: 'Applied', count: by.Applied, dot: 'Applied' },
    { key: 'Interview', label: 'Interview', count: by.Interview, dot: 'Interview' },
    { key: 'Offer', label: 'Offer', count: by.Offer, dot: 'Offer' },
    { key: 'Rejected', label: 'Rejected', count: by.Rejected, dot: 'Rejected' },
    { key: 'Filtered', label: 'Filtered', count: by.Filtered, dot: 'Filtered' },
    { key: 'Closed', label: 'Closed', count: by.Closed, dot: 'Closed' },
    { key: 'Discarded', label: 'Discarded', count: by.Discarded, dot: 'Discarded' },
  ];

  const bar = el('div', { class: 'filter-bar', role: 'tablist', 'aria-label': 'Filter applications' });
  for (const t of totals) {
    if (t.count === 0 && t.key !== 'All' && t.key !== 'Active') continue;
    const chip = el(
      'button',
      {
        class: `filter-chip${current === t.key ? ' active' : ''}`,
        role: 'tab',
        'aria-selected': current === t.key ? 'true' : 'false',
        onclick: () => onChange(t.key),
      },
      t.dot ? el('span', { class: `dot ${statusClass(t.dot)}` }) : null,
      t.label,
      el('span', { class: 'count' }, String(t.count)),
    );
    bar.appendChild(chip);
  }
  return bar;
}

function renderStatusPill(app: Application): HTMLElement {
  const select = el(
    'select',
    {
      class: 'status-select',
      'aria-label': 'Application status',
      onchange: async (e: Event) => {
        e.stopPropagation();
        const next = (e.target as HTMLSelectElement).value as Status;
        try {
          await updateStatus(app.id, next);
        } catch (err) {
          alert(`Could not update status: ${err instanceof Error ? err.message : String(err)}`);
        }
      },
      onclick: (e: Event) => e.stopPropagation(),
    },
    ...STATUSES.map((s) =>
      el('option', { value: s, selected: s === app.status }, s),
    ),
  );

  return el(
    'label',
    {
      class: 'status-pill',
      title: 'Change status',
      onclick: (e: Event) => e.stopPropagation(),
    },
    el('span', { class: `dot ${statusClass(app.status)}` }),
    select,
    el('span', { class: 'chev' }, '▾'),
  );
}

function renderFilesRow(
  app: Application,
  files: AppFiles | undefined,
  root: FileSystemDirectoryHandle | null,
): HTMLElement | null {
  if (!files || (!files.cv && !files.motivation && files.all.length === 0)) return null;
  const buttons: HTMLElement[] = [];
  const open = (name: string): EventListener => async (e: Event) => {
    e.stopPropagation();
    if (!root) return;
    try {
      await openFile(root, app, name);
    } catch (err) {
      alert(`Could not open: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
  if (files.cv) {
    buttons.push(
      el(
        'button',
        { class: 'btn tiny', onclick: open(files.cv), title: files.cv },
        el('span', { class: 'text-accent' }, 'CV'),
        files.cv,
      ),
    );
  }
  if (files.motivation) {
    buttons.push(
      el(
        'button',
        { class: 'btn tiny', onclick: open(files.motivation), title: files.motivation },
        el('span', { class: 'text-accent' }, 'Cover'),
        files.motivation,
      ),
    );
  }
  if (buttons.length === 0 && files.all.length > 0) {
    buttons.push(
      el(
        'span',
        { class: 'text-faint', style: 'font-size: 0.7rem; padding: 0.3rem 0;' },
        `${files.all.length} file${files.all.length === 1 ? '' : 's'} in folder`,
      ),
    );
  }
  return el('div', { class: 'app-files-row' }, ...buttons);
}

function renderCard(
  app: Application,
  files: AppFiles | undefined,
  root: FileSystemDirectoryHandle | null,
): HTMLElement {
  const badge = isEasyApply(app.applyType)
    ? el('span', { class: 'apply-type-badge easy' }, 'Easy Apply')
    : el('span', { class: 'apply-type-badge external' }, 'External');

  const removeBtn = el(
    'button',
    {
      class: 'btn ghost tiny',
      title: 'Remove from list (files on disk are kept)',
      'aria-label': 'Remove from list',
      onclick: async (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
        if (!confirm(`Remove "${app.jobTitle}" from the list? Files on disk are kept.`)) return;
        try {
          await removeApplication(app.id);
        } catch (err) {
          alert(`Could not remove: ${err instanceof Error ? err.message : String(err)}`);
        }
      },
    },
    '×',
  );

  const card = el(
    'a',
    {
      class: `app-card${app.status === 'Discarded' ? ' discarded' : ''}`,
      href: `#/job?id=${app.id}`,
    },
    el(
      'div',
      { class: 'app-main' },
      el('div', { class: 'app-company' }, badge, app.companyName),
      el('div', { class: 'app-title' }, app.jobTitle),
      el(
        'div',
        { class: 'app-meta' },
        el('span', { title: formatDate(app.savedAt) }, relativeDate(app.savedAt)),
        app.notes ? el('span', { class: 'text-faint' }, '·') : null,
        app.notes ? el('span', { class: 'text-faint', title: app.notes }, truncate(app.notes, 60)) : null,
      ),
    ),
    el(
      'div',
      { class: 'app-controls' },
      renderStatusPill(app),
      removeBtn,
    ),
  );

  const filesRow = renderFilesRow(app, files, root);
  if (filesRow) card.appendChild(filesRow);
  return card;
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

function renderEmptyState(): HTMLElement {
  return el(
    'div',
    { class: 'empty-state' },
    el(
      'div',
      { class: 'glyph' },
      'Appli',
      el('span', { class: 'slash' }, '/'),
      'fy',
    ),
    el('h2', {}, 'Nothing here yet — let\'s save your first job.'),
    el(
      'p',
      {},
      'Applify pairs a browser extension with this dashboard. Capture jobs as you browse, then come back here to track, generate documents, and prep for interviews.',
    ),
    el(
      'div',
      { class: 'teach-grid' },
      teachCard('1', 'Install the extension', 'Chrome, Edge, Brave, Arc, Opera. One click from the Chrome Web Store.'),
      teachCard('2', 'Browse a job page', 'LinkedIn, Workday, Glassdoor, Greenhouse, Lever, JobScout24, startup.jobs and more.'),
      teachCard('3', 'Click "Save job"', 'The job lands here. From the job page you can generate a tailored CV, cover letter, or interview prep.'),
    ),
    el(
      'div',
      { class: 'actions-row', style: 'margin-top: 0.5rem;' },
      el(
        'a',
        {
          class: 'btn primary',
          href: 'https://chromewebstore.google.com/detail/applify/pdjejhfebimebgfealafmebbabjnhkfg',
          target: '_blank',
          rel: 'noreferrer',
        },
        'Install the extension →',
      ),
      el('a', { class: 'btn', href: '#/settings' }, 'Add your profile'),
    ),
  );
}

function teachCard(num: string, title: string, desc: string): HTMLElement {
  return el(
    'div',
    { class: 'teach-card' },
    el('div', { class: 'num' }, num),
    el('div', { class: 'title' }, title),
    el('div', { class: 'desc' }, desc),
  );
}

export interface DashboardOptions {
  manifest: Manifest;
  root: FileSystemDirectoryHandle | null;
  journey: JourneyInputs;
}

export function renderDashboard(opts: DashboardOptions): HTMLElement {
  const { manifest, root, journey } = opts;
  const apps = manifest.applications;

  const container = el('section', { class: 'page' });

  container.appendChild(
    el(
      'div',
      { class: 'dashboard-head' },
      el(
        'div',
        { class: 'title-block' },
        el('div', { class: 'eyebrow' }, 'Dashboard'),
        el('h1', {}, 'Your search.'),
        apps.length > 0
          ? el(
            'p',
            { class: 'text-secondary', style: 'font-size: 0.82rem;' },
            `${apps.length} application${apps.length === 1 ? '' : 's'} tracked. ${countInMotion(apps)} in motion.`,
          )
          : null,
      ),
      apps.length > 0
        ? el(
          'div',
          { class: 'actions-row' },
          el('a', { class: 'btn', href: '#/settings' }, 'Profile & settings'),
        )
        : null,
    ),
  );

  const wizard = renderJourneyWizard(journey, apps);
  if (wizard) container.appendChild(wizard);

  if (apps.length === 0) {
    container.appendChild(renderEmptyState());
    return container;
  }

  container.appendChild(renderMetrics(apps));

  if (journey.walletConnected) {
    const bulk = renderBulkActions(apps);
    if (bulk) container.appendChild(bulk);
  }

  const sorted = [...apps].sort(
    (a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime(),
  );

  let filter: Filter = loadFilter();
  const list = el('div', { class: 'app-list' });
  const filterBar = el('div', {});
  const cardNodes = new Map<string, HTMLElement>();
  const filesCache = new Map<string, AppFiles | undefined>();

  const repaint = (): void => {
    filterBar.replaceChildren(
      renderFilterBar(sorted, filter, (f) => {
        filter = f;
        saveFilter(f);
        repaint();
      }),
    );

    list.replaceChildren();
    cardNodes.clear();
    const visible = sorted.filter((a) => passesFilter(a, filter));
    if (visible.length === 0) {
      list.appendChild(
        el(
          'div',
          { class: 'empty-state', style: 'padding: 2.5rem 1.5rem;' },
          el(
            'p',
            {},
            'No applications match this filter. Try ',
            el(
              'a',
              {
                href: '#',
                onclick: (e: Event) => { e.preventDefault(); filter = 'All'; saveFilter(filter); repaint(); },
              },
              'show all',
            ),
            '.',
          ),
        ),
      );
      return;
    }
    for (const a of visible) {
      const node = renderCard(a, filesCache.get(a.id), root);
      cardNodes.set(a.id, node);
      list.appendChild(node);
    }
  };

  repaint();

  if (root) {
    void (async () => {
      const filesByApp = await Promise.all(
        sorted.map(async (a) => [a.id, await detectFiles(root, a, manifest.config)] as const),
      );
      for (const [id, files] of filesByApp) {
        filesCache.set(id, files);
        const app = sorted.find((a) => a.id === id);
        if (!app) continue;
        const existing = cardNodes.get(id);
        if (!existing) continue;
        const fresh = renderCard(app, files, root);
        existing.replaceWith(fresh);
        cardNodes.set(id, fresh);
      }
    })();
  }

  container.append(
    el(
      'div',
      { class: 'stack stack-sm' },
      el('div', { class: 'eyebrow' }, `Applications`),
      filterBar,
      list,
    ),
  );

  return container;
}

// "In motion" = actively progressing applications, matching the metric card
// (Applied + Interview). Excludes Saved, which is captured-but-not-yet-applied.
function countInMotion(apps: Application[]): number {
  return apps.filter((a) => a.status === 'Applied' || a.status === 'Interview').length;
}
